import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer, MultilineInput, TextInput, Trigger, Info, Tooltip, Icon, Icons } from '@silverfort/sf-components';
import { Exclusion } from '../../../api/types/Exclusions';
import FormItem from './FormItem';
import { Popper } from '@material-ui/core';
import IncidentExclusionContext, { TEMP_ID_PREFIX } from '../incidentExclusionContext';
import DiscardChangesPopup from '../DiscardChangesPopup/DiscardChangesPopup';
import { Entity } from '../../../../../api';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import EntitiesSearch from '../../../EntitiesSearch/EntitiesSearch';
import { v4 as uuidv4 } from 'uuid';

const generateTempId = () => `${TEMP_ID_PREFIX}${uuidv4()}`;

interface Props {
    exclusionData?: Exclusion;
    incidentKind: string;
    onClose?: () => void;
    isOpen: boolean;
}

const ExclusionFormDrawer: FC<Props> = ({ exclusionData, onClose, incidentKind, isOpen }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [excludedEntities, setExcludedEntities] = useState<Entity[]>([]);
    const [searchEntitiesMenuOpen, setSearchEntitiesMenuOpen] = useState(false);
    const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);
    const entitiesMenuAnchor = useRef<HTMLDivElement>(null);

    const { updateExclusion, createExclusion } = useContext(IncidentExclusionContext);

    const resetForm = useCallback(() => {
        if (exclusionData) {
            setName(exclusionData.name);
            setDescription(exclusionData.description);
            setExcludedEntities(exclusionData.excluded_entities);
        } else {
            setName('');
            setDescription('');
            setExcludedEntities([]);
        }
    }, [exclusionData]);

    useEffect(() => {
        resetForm();
    }, [resetForm]);

    const selectEntitiesButtonText = useMemo(() => {
        return excludedEntities?.length ? `${excludedEntities.length} scopes` : 'Select Entities';
    }, [excludedEntities]);

    const onCloseEntitiesSearch = () => {
        setSearchEntitiesMenuOpen(false);
    };

    const isDirty = () => {
        if (exclusionData) {
            const excludedGuids = new Set(exclusionData.excluded_guids);
            return (
                name !== exclusionData.name ||
                description !== exclusionData.description ||
                exclusionData.excluded_guids.length !== excludedEntities.length ||
                excludedEntities?.some((entity) => !excludedGuids.has(entity.guid))
            );
        } else {
            return name || description || excludedEntities?.length;
        }
    };

    const errors = useMemo(() => {
        const errors: string[] = [];
        const nameAllowedRegex = /^[ \-A-Za-z0-9]{1,255}$/;

        const trimmedName = name.trim();

        if (!trimmedName.length) {
            errors.push('Name is required');
        } else if (!nameAllowedRegex.test(trimmedName)) {
            errors.push(
                'Name can only contain letters, numbers, spaces, and hyphens and be maximum 255 characters long'
            );
        }
        if (!excludedEntities?.length) {
            errors.push('Main entities are required');
        }
        return errors;
    }, [excludedEntities?.length, name]);

    const saveExclusion = () => {
        const trimmedName = name.trim();
        const baseExclusion = {
            ...exclusionData,
            name: trimmedName,
            description: description.trim(),
            kind: incidentKind,
            excluded_entities: excludedEntities,
            excluded_guids: excludedEntities.map((entity) => entity.guid),
            id: exclusionData?.id
        };

        if (exclusionData) {
            updateExclusion(exclusionData.id, baseExclusion);
        } else {
            const createExclusionRequest = {
                ...baseExclusion,
                excluded_entities: excludedEntities,
                id: generateTempId(),
                is_active: true
            };
            createExclusion(createExclusionRequest);
        }
        onClose?.();
    };

    const onDiscardChanges = () => {
        resetForm();
        setIsDiscardPopupOpen(false);
        onClose?.();
    };

    const onCancelButtonClick = () => {
        if (isDirty()) {
            setIsDiscardPopupOpen(true);
        } else {
            onDiscardChanges();
        }
    };

    const headerDescriptionContent = (
        <div className="flex items-center gap-1 w-full">
            <span className='whitespace-nowrap'>
                Exclusion rules prevent this incident from reoccurring and remove it from all notification channels going forward.
            </span>
            <Tooltip title="Filtered scenarios will not appear in your incident list or trigger any notifications (including SIEM alerts, Silverfort event logs, or webhooks).">
                <span className="rounded-full flex items-center justify-center w-fit">
                    <Icon icon={Icons.CircleInfo} size={18} />
                </span>
            </Tooltip>
        </div>
    );

    return (
        <Drawer
            open={isOpen}
            onClose={onCancelButtonClick}
            anchor="right"
            contentClassName="p-6 w-full"
            headerTitle={exclusionData?.name ? `Edit Exclusion rule - ${exclusionData?.name}` : 'Create Exclusion rule'}
            headerDescription={headerDescriptionContent as unknown as string}
            footerActionTitle="Done"
            footerCloseTitle="Cancel"
            onAction={saveExclusion}
            footerActionProps={{ disabled: errors?.length > 0 || !isDirty() }}
            footerClassName="px-6 flex justify-end gap-2 h-[65px]">
            <div className="flex flex-col gap-4 border border-gray-200 rounded-lg p-6 min-w-[600px] w-full">
                <FormItem label="Name" className="w-full">
                    <TextInput value={name} onChange={setName} className="h-8" />
                </FormItem>
                <FormItem label="Main entities">
                    <Trigger
                        text={selectEntitiesButtonText}
                        size="small"
                        onClick={() => setSearchEntitiesMenuOpen(true)}
                    />
                    <div ref={entitiesMenuAnchor} />
                    <Popper
                        open={searchEntitiesMenuOpen}
                        anchorEl={entitiesMenuAnchor.current}
                        disablePortal={true}
                        placement={'bottom-start'}>
                        <div className="w-[500px] bg-sf-white">
                            <EntitiesSearch
                                onClose={onCloseEntitiesSearch}
                                setSelectedEntities={setExcludedEntities}
                                selectedEntities={excludedEntities}
                                selectedEntityTypes={[AD_ENTITY_TYPES.USER, AD_ENTITY_TYPES.SERVER]}
                                allowSelectTypes={false}
                            />
                        </div>
                    </Popper>
                </FormItem>
                <FormItem label="Description" className="w-full">
                    <MultilineInput
                        placeholder={'Enter description'}
                        value={description}
                        onChange={setDescription}
                        className="w-full"
                    />
                </FormItem>
            </div>
            <DiscardChangesPopup
                open={isDiscardPopupOpen}
                onDiscard={onDiscardChanges}
                onClose={() => setIsDiscardPopupOpen(false)}
            />
        </Drawer>
    );
};

export default ExclusionFormDrawer;
